import { INSPECT_QUERY_KEY, describeHandler } from "../inspector.js";
import "./handler.js";
//#region src/handler/composed.ts
/**
* Composes multiple navigation handlers into a single {@link ComposedHandler}.
* Composed handlers pass actions from one handler to the next.
*
* ```ts
* const handler = composeHandlers([
*   focusHandler(),
*   movementHandler(),
*   parentHandler(),
* ]);
* ```
*
* @see {@link https://fiveway.dev/guide/handlers#composing-handlers} for more information about composing handlers.
*/
function composeHandlers(handlers) {
	let chain = null;
	let end = handlers.length - 1;
	const last = handlers[end];
	if (last != null && "chain" in last) {
		chain = last.chain;
		end -= 1;
	}
	for (let i = end; i >= 0; i--) {
		const handler = handlers[i];
		if (handler == null) continue;
		chain = {
			handler,
			next: chain
		};
	}
	return createHandlerFromChain(chain);
}
function createHandlerFromChain(chain) {
	const composedHandler = (action, ctx) => {
		const executeLink = (link, nextNode, nextAction) => {
			if (nextNode != null && nextNode !== ctx.node.id) return ctx.next(nextNode, nextAction ?? action);
			if (link == null) return ctx.next();
			if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) describeLinkHandler(link.handler, action, ctx);
			return link.handler(nextAction ?? action, {
				node: ctx.node,
				next: executeLink.bind(null, link.next)
			});
		};
		return executeLink(chain);
	};
	composedHandler.compose = (handler) => createHandlerFromChain({
		handler,
		next: chain
	});
	composedHandler.chain = chain;
	return composedHandler;
}
function describeLinkHandler(handler, action, ctx) {
	if (action.kind === "query" && action.key === "inspect") {
		const value = [];
		handler({
			kind: "query",
			key: INSPECT_QUERY_KEY,
			value
		}, {
			node: ctx.node,
			next: () => null
		});
		if (value.length === 0) describeHandler(action, { name: handler.name });
	}
}
//#endregion
export { composeHandlers };

//# sourceMappingURL=composed.js.map